import React, { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, Users, Sparkles, Trash2, Timer, Radio, ArrowLeft, ChefHat, Bell, Star, Ban, ShieldCheck, Link2, Power } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

// Render URLs inside message text as clickable links (for review links etc.)
function renderMsgWithLinks(text) {
  const parts = String(text || "").split(/(https?:\/\/\S+|www\.\S+)/g);
  return parts.map((p, i) => {
    if (/^(https?:\/\/|www\.)/i.test(p)) {
      const href = p.startsWith("http") ? p : `https://${p}`;
      return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="underline font-bold break-all">{p}</a>;
    }
    return <span key={i}>{p}</span>;
  });
}

function useCountdown(endsAt) {
  const [ms, setMs] = useState(() => new Date(endsAt) - new Date());
  useEffect(() => {
    const t = setInterval(() => setMs(new Date(endsAt) - new Date()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (ms <= 0) return "Scaduta";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60); const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ---------- Chat pubblica ----------
function OwnerPublicChat({ restaurant }) {
  const [msgs, setMsgs] = useState([]); const [text, setText] = useState(""); const [sending, setSending] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [banned, setBanned] = useState([]);
  const [reviewUrl, setReviewUrl] = useState(null);
  const [sendingReview, setSendingReview] = useState(false);
  const bottomRef = useRef(null); const sinceRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/owner/chat${sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : ""}`);
        if (!alive) return;
        setChatEnabled(data.chat_enabled !== false);
        setBanned(data.banned_guests || []);
        setReviewUrl(data.review_url || null);
        if (data.messages.length) {
          setMsgs((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const merged = [...prev, ...data.messages.filter((m) => !seen.has(m.id))];
            if (merged.length) sinceRef.current = merged[merged.length - 1].ts;
            return merged;
          });
        }
      } catch {}
    };
    load(); const t = setInterval(load, 3000); return () => { alive = false; clearInterval(t); };
  }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try { await api.post("/owner/chat", { message: text }); setText(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };
  const toggleChat = async () => {
    try {
      const { data } = await api.post("/owner/chat/toggle", { enabled: !chatEnabled });
      setChatEnabled(data.chat_enabled);
      toast.success(data.chat_enabled ? "Chat riattivata" : "Chat disabilitata per tutti i guest");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const toggleBan = async (guest_id, guest_name, currentlyBanned) => {
    try {
      const { data } = await api.post("/owner/chat/ban", { guest_id, banned: !currentlyBanned });
      setBanned(data.banned_guests || []);
      toast.success(currentlyBanned ? `${guest_name} riabilitato in chat` : `${guest_name} bloccato dalla chat`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const sendReview = async () => {
    if (sendingReview) return;
    if (!reviewUrl) { toast.error('Configura prima il link nella tab "Recensioni"'); return; }
    setSendingReview(true);
    try { await api.post("/owner/chat/send-review"); toast.success("Richiesta recensione inviata in chat"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSendingReview(false); }
  };
  return (
    <div className="flex flex-col h-[60vh] max-h-[600px]">
      {/* Owner controls */}
      <div className="border-b border-[#E8E2D5] bg-white px-3 py-2.5 flex flex-wrap items-center gap-2">
        <button data-testid="owner-chat-toggle" onClick={toggleChat} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black ${chatEnabled ? "bg-[#3B503A] text-white" : "bg-[#8B0000] text-white"}`}>
          <Power size={12}/> {chatEnabled ? "Chat: attiva" : "Chat: disabilitata"}
        </button>
        <button data-testid="owner-chat-send-review" onClick={sendReview} disabled={sendingReview || !reviewUrl} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white disabled:opacity-50" title={reviewUrl ? "Invia messaggio con link recensione" : "Attiva Boost Recensioni e configura il link"}>
          <Star size={12}/> ⭐ Lascia una recensione
        </button>
        {!reviewUrl && <span className="text-[10px] text-[#8C580C] italic">Attiva Boost Recensioni nella tab "Recensioni"</span>}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FBF9F5]">
        {msgs.length === 0 && <div className="text-center text-[#6B625B] mt-10 text-sm">Ancora nessun messaggio. Rompi il ghiaccio dalla tua parte!</div>}
        {msgs.map((m) => {
          const mine = m.is_owner;
          const isBanned = banned.includes(m.guest_id);
          return (
            <div key={m.id} data-testid={`owner-chat-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white shadow-md" : "bg-white border border-[#E8E2D5]"}`}>
                <div className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ${mine ? "text-white/90" : "text-[#3B503A]"}`}>
                  {mine ? <><ChefHat size={10} className="inline mr-1"/> STAFF · {restaurant?.name}</> : (
                    <>
                      <span>{m.guest_name}{m.table_number ? ` · T${m.table_number}` : ""}</span>
                      {isBanned && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#8B0000] text-white">BLOCCATO</span>}
                      <button
                        data-testid={`owner-chat-ban-${m.guest_id}`}
                        onClick={() => toggleBan(m.guest_id, m.guest_name, isBanned)}
                        className={`ml-auto inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isBanned ? "bg-[#EAF0E9] text-[#3B503A]" : "bg-[#FDECE4] text-[#8B0000]"}`}
                        title={isBanned ? "Riabilita utente in chat" : "Blocca utente dalla chat"}>
                        {isBanned ? <><ShieldCheck size={10}/> Sblocca</> : <><Ban size={10}/> Blocca</>}
                      </button>
                    </>
                  )}
                </div>
                <div className="text-sm mt-0.5 break-words">{renderMsgWithLinks(m.message)}</div>
                <div className={`text-[9px] mt-0.5 ${mine ? "text-white/70" : "text-[#B8AFA5]"}`}>{fmtTime(m.ts)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>
      <div className="border-t border-[#E8E2D5] p-3 flex gap-2 bg-white">
        <input data-testid="owner-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi come STAFF (puoi inviare link)..." className="flex-1 rounded-full border border-[#E8E2D5] px-4 py-2 text-sm"/>
        <button data-testid="owner-chat-send" disabled={sending || !text.trim()} onClick={send} className="w-10 h-10 bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white rounded-full grid place-items-center disabled:opacity-50"><Send size={16}/></button>
      </div>
    </div>
  );
}

// ---------- DM privati ----------
function OwnerDMs({ restaurant }) {
  const [active, setActive] = useState([]); const [convs, setConvs] = useState([]); const [msgs, setMsgs] = useState([]);
  const [open, setOpen] = useState(null); const [text, setText] = useState("");
  const sinceRef = useRef(null); const bottomRef = useRef(null);
  const load = async () => {
    try {
      const [{ data: g }, { data: d }] = await Promise.all([
        api.get("/owner/guests/active"),
        api.get(`/owner/dm/all${sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : ""}`),
      ]);
      setActive(g.active); setConvs(d.conversations);
      if (d.messages.length) {
        setMsgs((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const merged = [...prev, ...d.messages.filter((m) => !seen.has(m.id))];
          if (merged.length) sinceRef.current = merged[merged.length - 1].ts;
          return merged;
        });
      }
    } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const openWith = async (guest_id) => {
    try {
      const { data } = await api.post("/owner/dm/open", { guest_id });
      setOpen(data.conversation_id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const send = async () => {
    if (!text.trim() || !open) return;
    try { await api.post("/owner/dm/message", { conversation_id: open, message: text }); setText(""); load(); }
    catch { toast.error("Errore invio"); }
  };

  const currentConv = convs.find((c) => c.id === open);
  const currentMsgs = msgs.filter((m) => m.conversation_id === open);
  const activeConvGuestIds = new Set(convs.map((c) => c.guest?.id));

  if (open && currentConv) {
    return (
      <div className="flex flex-col h-[60vh] max-h-[600px]">
        <div className="border-b border-[#E8E2D5] p-3 flex items-center gap-3 bg-white">
          <button data-testid="owner-dm-back" onClick={() => setOpen(null)} className="w-9 h-9 rounded-full bg-[#F0EBE1] grid place-items-center"><ArrowLeft size={16}/></button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-[#C85A32] font-bold">Chat privata</div>
            <div className="text-sm font-semibold">{currentConv.guest?.name}{currentConv.guest?.table_number ? ` · T${currentConv.guest.table_number}` : ""}</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FBF9F5]">
          {currentMsgs.length === 0 && <div className="text-center text-[#6B625B] mt-10 text-sm">Rompi il ghiaccio con {currentConv.guest?.name}!</div>}
          {currentMsgs.map((m) => {
            const mine = m.is_owner;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white" : "bg-white border border-[#E8E2D5]"}`}>
                  <div className="text-sm">{m.message}</div>
                  <div className={`text-[9px] mt-0.5 ${mine ? "text-white/70" : "text-[#B8AFA5]"}`}>{fmtTime(m.ts)}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef}/>
        </div>
        <div className="border-t border-[#E8E2D5] p-3 flex gap-2 bg-white">
          <input data-testid="owner-dm-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi al guest..." className="flex-1 rounded-full border border-[#E8E2D5] px-4 py-2 text-sm"/>
          <button data-testid="owner-dm-send" onClick={send} className="w-10 h-10 bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white rounded-full grid place-items-center"><Send size={16}/></button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5 min-h-[60vh] max-h-[600px] overflow-y-auto bg-[#FBF9F5]">
      {convs.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#3B503A] mb-2">Le tue chat aperte</div>
          <div className="space-y-2">{convs.map((c) => {
            const lastMsg = [...msgs].reverse().find((m) => m.conversation_id === c.id);
            return (
              <button key={c.id} data-testid={`owner-dm-conv-${c.id}`} onClick={() => setOpen(c.id)} className="w-full text-left rounded-2xl bg-white border border-[#E8E2D5] p-3 flex items-center gap-3 hover:border-[#C85A32] transition">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D99B26] to-[#C85A32] text-white grid place-items-center font-bold">{c.guest?.name?.[0]?.toUpperCase() || "?"}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{c.guest?.name}{c.guest?.table_number ? ` · T${c.guest.table_number}` : ""}</div>
                  <div className="text-xs text-[#6B625B] truncate">{lastMsg ? (lastMsg.is_owner ? "Tu: " : "") + lastMsg.message : "Nessun messaggio ancora"}</div>
                </div>
                {lastMsg && <div className="text-[10px] text-[#B8AFA5] font-mono">{fmtTime(lastMsg.ts)}</div>}
              </button>
            );
          })}</div>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B625B] mb-2 flex items-center gap-1"><Users size={11}/> Guest attivi ({active.length})</div>
        {active.length === 0 ? (
          <div className="text-sm text-[#6B625B] italic bg-white border border-[#E8E2D5] rounded-2xl p-4 text-center">Nessun guest connesso al momento. Attendi che qualcuno scansioni il QR.</div>
        ) : (
          <div className="space-y-2">{active.map((g) => {
            const already = activeConvGuestIds.has(g.id);
            return (
              <div key={g.id} data-testid={`owner-active-guest-${g.id}`} className="rounded-2xl bg-white border border-[#E8E2D5] p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#EAF0E9] text-[#3B503A] grid place-items-center font-bold relative">
                  {g.name[0]?.toUpperCase()}
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-[#3B503A] border-2 border-white"/>
                </div>
                <div className="flex-1"><div className="font-semibold text-sm">{g.name}</div>{g.table_number && <div className="text-xs text-[#6B625B]">Tavolo {g.table_number}</div>}</div>
                <button data-testid={`owner-open-dm-${g.id}`} onClick={() => openWith(g.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#3B503A] hover:bg-[#2f4030] text-white text-xs font-bold">
                  {already ? "Apri" : "Scrivi"}
                </button>
              </div>
            );
          })}</div>
        )}
        {restaurant && <div className="text-[10px] text-[#6B625B] mt-3 text-center italic">I guest non possono iniziare una chat privata con te — sei tu a scegliere quando scrivere.</div>}
      </div>
    </div>
  );
}

// ---------- Promozioni a tempo ----------
function PromoRow({ p, onCancel }) {
  const label = useCountdown(p.ends_at);
  const expired = !p.active;
  return (
    <div data-testid={`promo-row-${p.id}`} className={`rounded-2xl border p-4 ${expired ? "bg-[#F5F2EA] border-[#E8E2D5] opacity-70" : "bg-gradient-to-br from-[#FEF3D6] to-[#FDECE4] border-[#F4E3B8]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#8C580C]">Promo {expired ? "scaduta" : "attiva"}</div>
          <div className="font-serif-display text-xl font-bold text-[#231F1D] mt-0.5">{p.title}</div>
          {p.description && <div className="text-sm text-[#6B625B] mt-1">{p.description}</div>}
        </div>
        {!expired && (
          <div className="text-center">
            <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#231F1D] text-[#D99B26] font-mono font-black text-sm"><Timer size={12}/> {label}</div>
            <button data-testid={`promo-cancel-${p.id}`} onClick={() => onCancel(p.id)} className="mt-2 text-xs text-[#C85A32] font-semibold inline-flex items-center gap-1 hover:underline"><Trash2 size={11}/> Termina</button>
          </div>
        )}
      </div>
    </div>
  );
}

function OwnerPromos() {
  const [promos, setPromos] = useState([]); const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState(""); const [desc, setDesc] = useState(""); const [duration, setDuration] = useState(10); const [creating, setCreating] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/owner/promotions"); setPromos(data.promotions); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const create = async () => {
    if (!title.trim()) { toast.error("Serve un titolo"); return; }
    setCreating(true);
    try {
      await api.post("/owner/promotions", { title, description: desc, duration_minutes: Number(duration) });
      setTitle(""); setDesc(""); toast.success("Promo lanciata!"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setCreating(false); }
  };
  const cancel = async (id) => {
    try { await api.delete(`/owner/promotions/${id}`); toast.success("Promo terminata"); load(); }
    catch { toast.error("Errore"); }
  };

  const active = promos.filter((p) => p.active);
  const expired = promos.filter((p) => !p.active);

  return (
    <div className="p-4 space-y-5 min-h-[60vh] max-h-[600px] overflow-y-auto bg-[#FBF9F5]">
      <div className="rounded-3xl border-2 border-dashed border-[#D99B26] bg-[#FEF3D6]/40 p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#8C580C] flex items-center gap-1"><Radio size={11}/> Nuova promo a tempo</div>
        <div className="mt-3 space-y-2">
          <input data-testid="promo-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Tutti i cocktail a 3€" className="w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm"/>
          <input data-testid="promo-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Dettagli (opzionale)" className="w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm"/>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6B625B] font-semibold">Durata:</span>
            {[5, 10, 15, 30, 60].map((m) => (
              <button key={m} data-testid={`promo-dur-${m}`} onClick={() => setDuration(m)} className={`text-xs font-bold px-3 py-1.5 rounded-full border ${duration === m ? "bg-[#231F1D] text-[#D99B26] border-[#231F1D]" : "bg-white border-[#E8E2D5] text-[#6B625B]"}`}>{m} min</button>
            ))}
          </div>
          <button data-testid="promo-launch" onClick={create} disabled={creating || !title.trim()} className="w-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white rounded-full py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Sparkles size={16}/> {creating ? "Lancio..." : `Lancia per ${duration} min`}
          </button>
        </div>
      </div>

      {loading ? <div className="text-sm text-[#6B625B]">Caricamento...</div> : (
        <>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#3B503A] mb-2">In corso ({active.length})</div>
            {active.length === 0 ? <div className="text-sm text-[#6B625B] italic bg-white border border-[#E8E2D5] rounded-2xl p-4 text-center">Nessuna promo attiva. Lanciane una qui sopra!</div> :
              <div className="space-y-2">{active.map((p) => <PromoRow key={p.id} p={p} onCancel={cancel}/>)}</div>}
          </div>
          {expired.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B625B] mb-2">Storico</div>
              <div className="space-y-2">{expired.slice(0, 5).map((p) => <PromoRow key={p.id} p={p} onCancel={cancel}/>)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Notifiche push ----------
function OwnerNotifications() {
  const [title, setTitle] = useState(""); const [body, setBody] = useState(""); const [sending, setSending] = useState(false);
  const [list, setList] = useState([]);
  const load = async () => { try { const { data } = await api.get("/owner/notify"); setList(data.notifications); } catch {} };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  const send = async () => {
    if (!title.trim()) { toast.error("Titolo obbligatorio"); return; }
    setSending(true);
    try { await api.post("/owner/notify", { title, body }); toast.success("Notifica inviata a tutti i guest"); setTitle(""); setBody(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };
  return (
    <div className="p-4 space-y-5 min-h-[60vh] max-h-[600px] overflow-y-auto bg-[#FBF9F5]">
      <div className="rounded-3xl border-2 border-dashed border-[#3B503A] bg-[#EAF0E9]/50 p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#3B503A] flex items-center gap-1"><Bell size={11}/> Nuova notifica push</div>
        <p className="text-[11px] text-[#6B625B] mt-1">Tutti i guest che hanno il menu aperto ricevono una notifica del browser.</p>
        <div className="mt-3 space-y-2">
          <input data-testid="notify-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Ora arriva la carbonara!" className="w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm"/>
          <textarea data-testid="notify-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Dettagli (opzionale)" rows={2} className="w-full rounded-2xl border border-[#E8E2D5] bg-white px-4 py-2 text-sm"/>
          <button data-testid="notify-send" onClick={send} disabled={sending || !title.trim()} className="w-full bg-gradient-to-r from-[#3B503A] to-[#2f4030] text-white rounded-full py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Bell size={16}/> {sending ? "Invio..." : "Invia a tutti i guest"}
          </button>
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B625B] mb-2">Ultime inviate</div>
        {list.length === 0 ? <div className="text-sm text-[#6B625B] italic bg-white border border-[#E8E2D5] rounded-2xl p-4 text-center">Nessuna notifica ancora</div> :
          <div className="space-y-2">{list.map((n) => (
            <div key={n.id} data-testid={`notify-row-${n.id}`} className="rounded-2xl bg-white border border-[#E8E2D5] p-3">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-[#EAF0E9] text-[#3B503A] grid place-items-center flex-shrink-0"><Bell size={14}/></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{n.title}</div>
                  {n.body && <div className="text-xs text-[#6B625B] mt-0.5">{n.body}</div>}
                  <div className="text-[10px] text-[#B8AFA5] mt-1 font-mono">{fmtTime(n.ts)}</div>
                </div>
              </div>
            </div>
          ))}</div>
        }
      </div>
    </div>
  );
}

// ---------- Boost Recensioni (add-on 3€/giorno) ----------
export function OwnerReviewConfig() {
  const [enabled, setEnabled] = useState(false);
  const [google, setGoogle] = useState("");
  const [tripadvisor, setTripadvisor] = useState("");
  const [platform, setPlatform] = useState("google"); // google | tripadvisor
  const [initial, setInitial] = useState({ enabled: false, google: "", tripadvisor: "", platform: "google" });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/restaurant");
        const g = data.google_reviews_url || "";
        const t = data.tripadvisor_url || "";
        const p = data.active_review_platform || "google";
        const en = !!data.boost_reviews_enabled;
        setEnabled(en); setGoogle(g); setTripadvisor(t); setPlatform(p);
        setInitial({ enabled: en, google: g, tripadvisor: t, platform: p });
      } catch {}
    })();
  }, []);

  const dirty = enabled !== initial.enabled || google.trim() !== initial.google || tripadvisor.trim() !== initial.tripadvisor || platform !== initial.platform;
  const activeLink = platform === "tripadvisor" ? tripadvisor.trim() : google.trim();
  const savedActiveLink = initial.platform === "tripadvisor" ? initial.tripadvisor : initial.google;
  const canSend = initial.enabled && !!savedActiveLink && !dirty;

  const validUrl = (s) => !s || /^https?:\/\//i.test(s);

  const save = async () => {
    if (!validUrl(google.trim())) { toast.error("Link Google non valido (deve iniziare con http/https)"); return; }
    if (!validUrl(tripadvisor.trim())) { toast.error("Link TripAdvisor non valido (deve iniziare con http/https)"); return; }
    if (enabled && !activeLink) { toast.error(`Inserisci il link ${platform === "tripadvisor" ? "TripAdvisor" : "Google"} prima di attivare il servizio`); return; }
    setSaving(true);
    try {
      const payload = {
        boost_reviews_enabled: enabled,
        google_reviews_url: google.trim(),
        tripadvisor_url: tripadvisor.trim(),
        active_review_platform: platform,
      };
      await api.put("/restaurant", payload);
      setInitial({ enabled, google: google.trim(), tripadvisor: tripadvisor.trim(), platform });
      toast.success(enabled ? "Boost Recensioni attivato" : "Impostazioni salvate");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const quickSend = async () => {
    if (!canSend) { toast.error("Attiva il servizio e salva prima il link"); return; }
    setSending(true);
    try { await api.post("/owner/chat/send-review"); toast.success("Richiesta recensione inviata in chat pubblica"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="owner-review-tab" className="p-4 space-y-5 min-h-[60vh] max-h-[600px] overflow-y-auto bg-[#FBF9F5]">
      {/* Header card with price + toggle */}
      <div className="rounded-3xl border-2 border-dashed border-[#D99B26] bg-gradient-to-br from-[#FEF3D6]/60 to-white p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#8C580C] flex items-center gap-1">
              <Star size={11}/> Add-on · Boost Recensioni
            </div>
            <h3 className="font-serif-display text-xl font-bold text-[#231F1D] mt-1">Aumenta recensioni Google e TripAdvisor</h3>
            <p className="text-sm text-[#6B625B] mt-1 leading-relaxed">Il link scelto viene mostrato automaticamente a fine quiz (a chi vince e a chi non arriva primo) e può essere inviato in chat con un click.</p>
          </div>
          <div className="text-right">
            <div className="font-serif-display text-3xl font-black text-[#C85A32]">3€</div>
            <div className="text-[10px] font-bold text-[#6B625B] uppercase tracking-wider">/giorno</div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl bg-white/80 border border-[#E8E2D5] px-4 py-3">
          <div>
            <div className="text-sm font-bold text-[#231F1D]">Servizio</div>
            <div className="text-[11px] text-[#6B625B]">{enabled ? "Il link è visibile a fine quiz e disponibile in chat" : "Attiva per mostrare il link a fine quiz e in chat"}</div>
          </div>
          <button
            data-testid="boost-reviews-toggle"
            onClick={() => setEnabled((v) => !v)}
            className={`relative w-14 h-8 rounded-full transition ${enabled ? "bg-gradient-to-r from-[#D99B26] to-[#C85A32]" : "bg-[#D6CFC1]"}`}
          >
            <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${enabled ? "left-7" : "left-1"}`}/>
          </button>
        </div>
      </div>

      {/* Google */}
      <div className="rounded-2xl bg-white border border-[#E8E2D5] p-4">
        <label className="text-[10px] uppercase tracking-widest text-[#6B625B] font-bold flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-[#4285F4]"/> Link Google Reviews
        </label>
        <input
          data-testid="review-google-input"
          value={google}
          onChange={(e) => setGoogle(e.target.value)}
          placeholder="https://g.page/il-tuo-locale/review"
          className="mt-1 w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4285F4]"
        />
      </div>

      {/* TripAdvisor */}
      <div className="rounded-2xl bg-white border border-[#E8E2D5] p-4">
        <label className="text-[10px] uppercase tracking-widest text-[#6B625B] font-bold flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-[#00AF87]"/> Link TripAdvisor
        </label>
        <input
          data-testid="review-tripadvisor-input"
          value={tripadvisor}
          onChange={(e) => setTripadvisor(e.target.value)}
          placeholder="https://www.tripadvisor.it/UserReviewEdit-..."
          className="mt-1 w-full rounded-full border border-[#E8E2D5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00AF87]"
        />
      </div>

      {/* Platform selector */}
      <div className="rounded-2xl bg-white border border-[#E8E2D5] p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#6B625B] font-bold">Piattaforma attiva</div>
        <div className="text-[11px] text-[#6B625B] mt-0.5">Scegli quale link usare per il quiz e per la chat</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            data-testid="review-platform-google"
            onClick={() => setPlatform("google")}
            className={`rounded-2xl px-3 py-3 text-sm font-bold border-2 transition ${platform === "google" ? "border-[#4285F4] bg-[#4285F4]/10 text-[#231F1D]" : "border-[#E8E2D5] bg-white text-[#6B625B] hover:border-[#B8AFA5]"}`}
          >
            🌐 Google
          </button>
          <button
            data-testid="review-platform-tripadvisor"
            onClick={() => setPlatform("tripadvisor")}
            className={`rounded-2xl px-3 py-3 text-sm font-bold border-2 transition ${platform === "tripadvisor" ? "border-[#00AF87] bg-[#00AF87]/10 text-[#231F1D]" : "border-[#E8E2D5] bg-white text-[#6B625B] hover:border-[#B8AFA5]"}`}
          >
            🦉 TripAdvisor
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button data-testid="review-save" onClick={save} disabled={saving || !dirty} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#3B503A] text-white text-sm font-bold disabled:opacity-50">
          <Link2 size={13}/> {saving ? "Salvo..." : "Salva impostazioni"}
        </button>
        <button data-testid="review-quick-send" onClick={quickSend} disabled={sending || !canSend} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white text-sm font-bold disabled:opacity-50">
          <Send size={13}/> {sending ? "Invio..." : "⭐ Lascia una recensione (invia in chat)"}
        </button>
      </div>

      {/* Preview */}
      <div className="rounded-2xl bg-white border border-[#E8E2D5] p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B625B]">Anteprima messaggio chat</div>
        <div className="mt-2 rounded-2xl bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white p-3 text-sm">
          ⭐ Lascia una recensione e aiuta il nostro locale a crescere! 🙏{" "}
          {activeLink ? (
            <a href={activeLink} target="_blank" rel="noopener noreferrer" className="underline font-bold break-all">{activeLink}</a>
          ) : (
            <span className="italic opacity-80">[configura il link {platform === "tripadvisor" ? "TripAdvisor" : "Google"}]</span>
          )}
        </div>
        <div className="mt-3 text-[11px] text-[#6B625B]">
          Il pulsante <b>⭐ Lascia una recensione</b> apparirà anche automaticamente nella pagina premi del quiz (per chi vince e per chi non arriva primo).
        </div>
      </div>
    </div>
  );
}

// ---------- Wrapper ----------
export default function OwnerChat({ restaurant, me }) {
  const [sub, setSub] = useState("chat"); // chat | dm | promo | notify | review
  const tabs = [
    { id: "chat", label: "Chat pubblica", icon: MessageCircle },
    { id: "dm", label: "DM privati", icon: Users },
    { id: "promo", label: "Promo", icon: Sparkles },
    { id: "notify", label: "Notifiche", icon: Bell },
    { id: "review", label: "Recensioni", icon: Star },
  ];
  const voiceOnlyBlocked = me?.plan === "voice_only" || me?.plan === "expired" || me?.plan === "pending";
  if (voiceOnlyBlocked) {
    return (
      <div data-testid="chat-blocked-panel" className="bg-white rounded-3xl border-2 border-dashed border-[#D99B26] p-6 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#FEF3D6] text-[#8C580C] grid place-items-center"><Radio size={22}/></div>
        <h3 className="font-serif-display text-xl font-bold mt-3 text-[#231F1D]">Chat & Promo bloccata</h3>
        <p className="text-sm text-[#6B625B] mt-1">Le funzioni Chat pubblica, DM, Promo e Notifiche sono incluse nel piano Full (o durante il trial). Richiedi l'upgrade all'amministratore.</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-3xl border border-[#E8E2D5] shadow-sm overflow-hidden">
      <div className="p-5 pb-3 border-b border-[#E8E2D5]">
        <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">Comunicazione live</div>
        <h3 className="font-serif-display text-2xl font-bold mt-1">Chat & Promo</h3>
      </div>
      <div className="flex border-b border-[#E8E2D5] overflow-x-auto no-scrollbar">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`owner-tab-${t.id}`} onClick={() => setSub(t.id)}
              className={`flex-1 min-w-[80px] py-3 text-xs sm:text-sm font-bold inline-flex items-center justify-center gap-1.5 ${sub === t.id ? "text-[#C85A32] border-b-2 border-[#C85A32] bg-[#FDECE4]/40" : "text-[#6B625B] hover:bg-[#FBF9F5]"}`}>
              <Icon size={15}/> <span>{t.label.split(" ")[0]}</span>
            </button>
          );
        })}
      </div>
      {sub === "chat" && <OwnerPublicChat restaurant={restaurant}/>}
      {sub === "dm" && <OwnerDMs restaurant={restaurant}/>}
      {sub === "promo" && <OwnerPromos/>}
      {sub === "notify" && <OwnerNotifications/>}
      {sub === "review" && <OwnerReviewConfig/>}
    </div>
  );
}
